import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { APP_VERSION } from '../version';
import { getAffiliateBySlug, officialSupportGroup } from '../services/affiliateService';

/* ═══════════════════════════════════════════════════
   ExtensionView — halaman khas muat turun + pasang extension
   Route: /extension (perlu login — sengaja TIADA dalam pokok route awam)

   SESAAT TIDAK diedar melalui Chrome Web Store — pelanggan muat turun
   ZIP (public/SESAAT-EXTENSION.zip) dan pasang sendiri guna "Load unpacked".
   Sebab itu langkah pasang ada di halaman ini, bukan sekadar butang download.

   Versi dibaca dari src/version.js (APP_VERSION) — satu sumber, sama macam
   butang di Dashboard Step 1.
   ═══════════════════════════════════════════════════ */

const TUTORIAL_PLAYLIST_URL = 'https://www.youtube.com/playlist?list=PLUgKht1i8s1Yo9oEW_L4yUE4o_NWx1oIX';

const STEPS = [
    {
        icon: 'download',
        title: 'Download fail ZIP',
        body: 'Klik butang download di atas. Fail SESAAT-EXTENSION.zip akan masuk ke folder Downloads anda.',
    },
    {
        icon: 'folder_zip',
        title: 'Unzip fail',
        body: 'Klik kanan pada fail ZIP tadi, pilih Extract All. Simpan folder hasil extract di tempat tetap — contoh folder Documents.',
        note: 'Jangan install terus dari dalam ZIP. Jangan padam folder ini selepas install — Chrome baca folder tersebut setiap kali browser dibuka.',
    },
    {
        icon: 'toggle_on',
        title: 'Hidupkan Developer mode',
        body: 'Buka tab baharu, taip chrome://extensions pada address bar. Hidupkan suis Developer mode di penjuru kanan atas.',
    },
    {
        icon: 'extension',
        title: 'Klik Load unpacked',
        body: 'Pilih folder yang di-extract tadi. Extension SESAAT terus muncul dalam senarai.',
        note: 'Kalau Chrome papar mesej Manifest file is missing or unreadable, folder yang dipilih salah. Pilih folder dalam yang ada fail manifest.json.',
    },
];

const FAQS = [
    {
        q: 'Macam mana nak update ke versi baharu?',
        a: 'Buka chrome://extensions, klik Remove pada extension SESAAT yang lama. Kemudian ulang langkah 1 hingga 4 guna fail ZIP terbaharu. Produk dan data anda selamat — semuanya simpan dalam akaun, bukan dalam extension.',
    },
    {
        q: 'Extension hilang selepas Chrome ditutup.',
        a: 'Ini jadi bila folder hasil extract dipadam atau dialih. Simpan folder tersebut di tempat tetap, kemudian install semula guna Load unpacked.',
    },
    {
        q: 'Panel SESAAT tak muncul.',
        a: 'Klik icon extension pada toolbar Chrome untuk buka panel. Kalau halaman sudah terbuka sebelum extension di-install, refresh halaman tersebut dahulu.',
    },
    {
        q: 'Kena install pada setiap browser?',
        a: 'Ya. Setiap browser atau profile Chrome yang anda guna kena install berasingan, dan login guna akaun SESAAT yang sama.',
    },
];

const ExtensionView = ({ onMenuClick, isPaid = false }) => {
    // Link bantuan — keutamaan: (1) group ejen yang merujuk, kalau ada;
    // (2) group rasmi SESAAT ikut tier — Free trial vs Premium/Fighter.
    const [supportLink, setSupportLink] = useState(() => officialSupportGroup(isPaid));
    useEffect(() => {
        setSupportLink(officialSupportGroup(isPaid)); // tier berubah → kira semula
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', uid));
                const refCode = snap.exists() ? snap.data()?.referredBy : null;
                if (!refCode) return;
                const aff = await getAffiliateBySlug(refCode);
                if (!cancelled && aff?.supportGroupLink) setSupportLink(aff.supportGroupLink);
            } catch (_) { /* senyap — fallback group rasmi sudah memadai */ }
        })();
        return () => { cancelled = true; };
    }, [isPaid]);

    return (
        <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">

                {/* ─── Header ─── */}
                <div className="flex items-center gap-3 mb-8">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            aria-label="Buka menu"
                            className="md:hidden -ml-1.5 w-10 h-10 flex items-center justify-center rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 24 }}>menu</span>
                        </button>
                    )}
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 shrink-0">
                        <span className="material-symbols-outlined text-[22px] sm:text-[24px]" style={{ fontVariationSettings: "'FILL' 1" }}>extension</span>
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Extension</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Download dan install extension SESAAT</p>
                    </div>
                </div>

                {/* ═══════════════ HERO — muat turun ═══════════════ */}
                <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 px-6 sm:px-10 pt-10 sm:pt-14 pb-0 mb-10 shadow-xl shadow-indigo-500/20">
                    {/* Hiasan latar */}
                    <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-10 w-64 h-64 rounded-full bg-violet-300/20 blur-3xl pointer-events-none" />

                    <div className="relative text-center">
                        <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight mb-3">
                            SESAAT Browser Extension
                        </h2>
                        <p className="text-indigo-100 text-sm sm:text-base max-w-lg mx-auto mb-8">
                            Automate Google Flow dan upload TikTok terus dari browser anda.
                        </p>

                        {/* Butang muat turun */}
                        <a
                            href="/SESAAT-EXTENSION.zip"
                            download="SESAAT-EXTENSION.zip"
                            className="inline-flex items-center gap-3 px-7 sm:px-9 py-4 rounded-2xl bg-white text-indigo-700 font-bold text-sm sm:text-base shadow-xl shadow-indigo-900/20 hover:-translate-y-0.5 hover:shadow-2xl active:scale-[0.98] transition-all duration-300"
                        >
                            <span className="material-symbols-outlined text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>download</span>
                            Download Extension
                            <span className="text-[11px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 leading-none">
                                V{APP_VERSION}
                            </span>
                        </a>
                        <p className="text-indigo-200 text-xs mt-4">
                            Untuk Google Chrome dan Microsoft Edge di komputer
                        </p>

                        {/* Ilustrasi browser + side panel */}
                        <div className="mt-10 mx-auto max-w-2xl rounded-t-2xl bg-white dark:bg-slate-900 shadow-2xl shadow-indigo-900/30 overflow-hidden">
                            {/* Bar browser */}
                            <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                                <div className="flex-1 mx-3 h-6 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center px-3">
                                    <span className="text-[10px] text-slate-400 truncate">labs.google/fx/tools/flow</span>
                                </div>
                                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-white text-[13px]" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
                                </div>
                            </div>
                            {/* Kandungan: halaman + side panel */}
                            <div className="flex h-44 sm:h-52">
                                <div className="flex-1 bg-slate-50 dark:bg-slate-950 p-4 grid grid-cols-3 gap-2">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="rounded-lg bg-slate-200/70 dark:bg-slate-800" />
                                    ))}
                                </div>
                                <div className="w-32 sm:w-40 shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                                    <div className="px-3 py-2 bg-gradient-to-r from-indigo-600 to-violet-600">
                                        <p className="text-[10px] font-black text-white tracking-wide">⚡ SESAAT</p>
                                    </div>
                                    <div className="p-3 space-y-2">
                                        <div className="h-3 rounded bg-slate-200 dark:bg-slate-800" />
                                        <div className="h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-800" />
                                        <div className="h-8 rounded-lg bg-indigo-100 dark:bg-indigo-500/20" />
                                        <div className="h-3 rounded bg-slate-200 dark:bg-slate-800" />
                                        <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-800" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═══════════════ CARA PASANG ═══════════════ */}
                <section className="mb-10">
                    <div className="mb-6">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 dark:text-indigo-400 mb-2">Install</p>
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white mb-2">Cara install dalam 4 langkah</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
                            SESAAT tiada dalam Chrome Web Store. Anda install sendiri guna fail ZIP — sekali sahaja, ambil masa lebih kurang 2 minit.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {STEPS.map((step, i) => (
                            <div
                                key={step.title}
                                className="relative bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-6 pt-7 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-lg hover:shadow-indigo-500/5 transition-all duration-300"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="shrink-0 w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-indigo-500 text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>{step.icon}</span>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-[10px] font-bold shrink-0">{i + 1}</span>
                                            <h3 className="text-base font-bold text-slate-800 dark:text-white">{step.title}</h3>
                                        </div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{step.body}</p>
                                        {step.note && (
                                            <div className="mt-3 flex gap-2 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200/70 dark:border-amber-500/20 px-3 py-2">
                                                <span className="material-symbols-outlined text-amber-500 text-[16px] shrink-0 mt-px" style={{ fontVariationSettings: "'FILL' 1" }}>info</span>
                                                <p className="text-xs text-amber-800 dark:text-amber-300/90 leading-relaxed">{step.note}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Selepas pasang */}
                    <div className="mt-4 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 p-5 flex gap-3">
                        <span className="material-symbols-outlined text-emerald-500 text-[20px] shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                        <div>
                            <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-300 mb-1">Selepas install</h3>
                            <p className="text-sm text-emerald-700/90 dark:text-emerald-300/80 leading-relaxed">
                                Klik icon extension pada toolbar Chrome untuk buka panel SESAAT. Login guna akaun yang sama macam di laman ini. Panel akan buka di tepi browser.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ═══════════════ MASALAH BIASA ═══════════════ */}
                <section className="mb-10">
                    <div className="mb-6">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 dark:text-indigo-400 mb-2">Bantuan</p>
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Masalah biasa</h2>
                    </div>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 divide-y divide-slate-200/80 dark:divide-slate-800 overflow-hidden">
                        {FAQS.map((item) => (
                            <details key={item.q} className="group">
                                <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer list-none hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                    <span className="material-symbols-outlined text-slate-400 text-[20px] transition-transform group-open:rotate-90">chevron_right</span>
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{item.q}</span>
                                </summary>
                                <p className="px-5 pb-5 pl-14 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                {/* ═══════════════ CTA — tutorial + sokongan ═══════════════ */}
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-10">
                    <a
                        href={TUTORIAL_PLAYLIST_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-6 hover:border-red-300 dark:hover:border-red-500/40 hover:shadow-lg transition-all duration-300"
                    >
                        <div className="w-11 h-11 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-red-500 text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-800 dark:text-white mb-1">Video tutorial</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-3">
                            Playlist ringkas — install extension, guna workspace, flow automation dan upload TikTok.
                        </p>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-500">
                            Tonton playlist
                            <span className="material-symbols-outlined text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
                        </span>
                    </a>

                    <a
                        href={supportLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-6 hover:border-emerald-300 dark:hover:border-emerald-500/40 hover:shadow-lg transition-all duration-300"
                    >
                        <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-emerald-500 text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>support_agent</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-800 dark:text-white mb-1">Masih tersekat?</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-3">
                            Tanya dalam group WhatsApp. Team support akan tolong anda install step by step.
                        </p>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600">
                            Buka group WhatsApp
                            <span className="material-symbols-outlined text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
                        </span>
                    </a>
                </section>

            </div>
        </div>
    );
};

export default ExtensionView;
